import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faExclamationTriangle } from "@fortawesome/pro-solid-svg-icons/faExclamationTriangle";
import { faEye } from "@fortawesome/pro-solid-svg-icons/faEye";
import { faEyeSlash } from "@fortawesome/pro-solid-svg-icons/faEyeSlash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Lead, Territory } from "@mapmycustomers/shared/types/entity";
import { DuplicateEntity } from "@mapmycustomers/shared/types/entity/Duplicate";
import { Button } from "@mapmycustomers/ui";

import DuplicatesTable from "@app/component/DuplicatesTable";
import EntityBadge from "@app/component/EntityBadge";
import EntityModal from "@app/component/modal/EntityModal";
import {
  hideDuplicates,
  hideLeads,
  showHiddenLeads,
} from "@app/scene/map/store/leadFinder/actions";
import {
  areDuplicatesVisible,
  getDuplicatesRecords,
  getPrimaryRecord,
} from "@app/scene/map/store/leadFinder/selectors";
import { doesOrganizationSupportTerritories } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getTerritories, getTerritoryCount } from "@app/store/territories";

import styles from "./DuplicatesModal.module.scss";

const NUMBER_OF_TERRITORIES_TO_SHOW = 3;

interface Props {
  hideDuplicates: typeof hideDuplicates;
  onHideLead: typeof hideLeads.request;
  onShowLead: typeof showHiddenLeads.request;
  orgHasTerritories: boolean;
  primaryRecord?: Lead;
  records?: DuplicateEntity[];
  territories: Territory[];
  visible: boolean;
}

const DuplicatesModal: React.FC<Props> = ({
  hideDuplicates,
  onHideLead,
  onShowLead,
  orgHasTerritories,
  primaryRecord,
  records,
  territories,
  visible,
}) => {
  const intl = useIntl();

  const handleHide = useCallback(() => {
    hideDuplicates();
  }, [hideDuplicates]);

  const handleToggle = useCallback(() => {
    if (primaryRecord) {
      (primaryRecord.blackListed ? onShowLead : onHideLead)({ placeIds: [primaryRecord.place_id] });
    }
  }, [onHideLead, onShowLead, primaryRecord]);

  return visible ? (
    <EntityModal className={styles.container} footer={null} onCancel={handleHide}>
      <div className={styles.bodyContainer}>
        <FontAwesomeIcon
          className={cn(styles.icon, {
            [styles.errorIcon]: !primaryRecord?.isInTerr && orgHasTerritories,
          })}
          icon={faExclamationTriangle}
          size="3x"
        />
        <span className={styles.title}>
          {intl.formatMessage({
            id: "map.tool.leadFinder.duplicate.modal.heading",
            defaultMessage: "Warning",
            description: "Title of Potential Duplicate Lead modal",
          })}
        </span>

        {primaryRecord && !primaryRecord.isInTerr && orgHasTerritories && (
          <>
            <div className={cn(styles.section, styles.territoriesSection)}>
              <div>
                {intl.formatMessage(
                  {
                    id: "map.tool.leadFinder.duplicate.modal.territories.title",
                    defaultMessage:
                      "{icon} Lead is outside your {count, plural, one {territory} other {territories}}",
                    description: "Title of Territories section in Lead finder duplicates modal",
                  },
                  {
                    count: territories.length,
                    icon: (
                      <FontAwesomeIcon
                        className={styles.territoriesIcon}
                        icon={faExclamationCircle}
                      />
                    ),
                  }
                )}
              </div>
            </div>
            <div className={styles.sectionContent}>
              <div className={styles.territoriesDescription}>
                {intl.formatMessage(
                  {
                    id: "map.tool.leadFinder.duplicate.modal.territories.description",
                    defaultMessage:
                      "This lead is not located in any of the following {count, plural, one {territory} other {territories}}:",
                    description:
                      "Description of Territories section in Lead finder duplicates modal",
                  },
                  { count: territories.length }
                )}
              </div>
              {territories.slice(0, NUMBER_OF_TERRITORIES_TO_SHOW).map((territory) => (
                <div className={styles.territory} key={territory.id}>
                  <EntityBadge className={styles.territoryBadge} entity={territory} size="small" />
                </div>
              ))}
              {territories.length > NUMBER_OF_TERRITORIES_TO_SHOW ? (
                <div className={styles.territoriesFooter}>
                  {intl.formatMessage(
                    {
                      id: "map.tool.leadFinder.duplicate.modal.territories.footer",
                      defaultMessage: "... and {count} more",
                      description: "Footer of Territories section in Lead finder duplicates modal",
                    },
                    { count: territories.length - NUMBER_OF_TERRITORIES_TO_SHOW }
                  )}
                </div>
              ) : null}
            </div>
          </>
        )}

        {records && records.length > 0 && (
          <>
            <div className={cn(styles.section, styles.duplicatesSection)}>
              <div>
                {intl.formatMessage(
                  {
                    id: "map.tool.leadFinder.duplicate.modal.duplicates.title",
                    defaultMessage:
                      "{icon} Lead may already exist{hidden, select, true { and has been hidden} other {}}",
                    description: "Title of Duplicates section in Lead finder duplicates modal",
                  },
                  {
                    hidden: primaryRecord?.blackListed,
                    icon: (
                      <FontAwesomeIcon
                        className={styles.duplicationIcon}
                        icon={faExclamationCircle}
                      />
                    ),
                  }
                )}
              </div>
              <div className={styles.sectionActions}>
                <Button onClick={handleToggle} type="link">
                  {intl.formatMessage(
                    {
                      id: "map.tool.leadFinder.duplicate.modal.duplicates.toggle",
                      defaultMessage:
                        "{hidden, select, true {Unhide lead} other {Hide lead}} {icon}",
                      description: "Toggle lead visibility button in Lead finder duplicates modal",
                    },
                    {
                      hidden: primaryRecord?.blackListed,
                      icon: (
                        <FontAwesomeIcon icon={primaryRecord?.blackListed ? faEye : faEyeSlash} />
                      ),
                    }
                  )}
                </Button>
              </div>
            </div>
            <div className={styles.sectionContent}>
              <DuplicatesTable duplicates={records ?? []} showAsLinks showHeader={false} />
            </div>
          </>
        )}
      </div>
    </EntityModal>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  orgHasTerritories: doesOrganizationSupportTerritories(state) && getTerritoryCount(state) > 0,
  primaryRecord: getPrimaryRecord(state),
  records: getDuplicatesRecords(state),
  territories: getTerritories(state),
  visible: areDuplicatesVisible(state),
});

const mapDispatchToProps = {
  hideDuplicates,
  onHideLead: hideLeads.request,
  onShowLead: showHiddenLeads.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DuplicatesModal);
